import React, { Children, FunctionComponent } from 'react';
import { useCss, IStyles } from 'app/utils/css';

interface IProps {
  margin?: string;
  center?: boolean;
  wrap?: boolean;
  grow?: boolean;
  vertical?: boolean;
  centerVertically?: boolean;
  disableNegativeMargins?: boolean;
  /**
   * CSS properties for child elements
   */
  childCss?: (idx: number) => IStyles;
}

const FlexMarginGroup: FunctionComponent<IProps> = ({
  children,
  margin = '10px',
  center = false,
  centerVertically = false,
  wrap = false,
  vertical = false,
  grow = false,
  disableNegativeMargins = false,
  childCss,
}) => {
  const css = useCss();
  return (
    <div className={css('container', {
      display:        'flex',
      flexGrow:       grow ? 1 : 0,
      flexDirection:  vertical ? 'column' : 'row',
      flexWrap:       wrap ? 'wrap' : 'nowrap',
      justifyContent: center ? 'center' : 'flex-start',
      alignItems: centerVertically ? 'center' : undefined,
      // negative margin to cancel outer margins of outer-most children
      // Only added on the sides that are affected by the orientation. Otherwise negative margins
      // can cause elements to extend past the boundaries of the document.
      margin: disableNegativeMargins ? 0 :
                wrap ? `calc(${margin} / -2)` :
                vertical ? `calc(${margin} / -2) 0` : `0 calc(${margin} / -2)`,
    })}
    >
      {Children.map(children, (child, i) =>
        // We are assuming that Children.map is converting falsy children to null.
        // We WANT to let 0, NaN, '' etc through (to match React's behavior) so `child && ...` is not enough
        child !== null && (
          <div
            key={i}
            className={css('child', {
              ...(childCss && childCss(i)),

              ':empty': {
                display: 'none',
              },
              // Only adding a margin on the side that is affected by the orientation. Otherwise, negative margin
              // can cause elements to extend past the boundaries of the document.
              margin: wrap ? `calc(${margin} / 2)` : vertical ? `calc(${margin} / 2) 0` : `0 calc(${margin} / 2)`,
            })}
          >
            {child}
          </div>
        ),
      )}
    </div>
  );
};

export default FlexMarginGroup;
